import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { initMercadoPago, CardPayment } from "@mercadopago/sdk-react";
import { toast } from "sonner";
import { ArrowLeft, Shield, Zap, Check, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TIER_META = {
  start:      { name: "Start",      price: 89,   limit: 100 },
  pro:        { name: "Pro",        price: 169,  limit: 500, highlighted: true },
  business:   { name: "Business",   price: 349,  limit: 2500 },
  enterprise: { name: "Enterprise", price: 699,  limit: 5000 },
  agencias:   { name: "Agências",   price: 1299, limit: 10000 },
};

export default function Checkout() {
  const navigate = useNavigate();
  const { tier: tierParam } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tier, setTier] = useState(tierParam || "pro");
  const [mpReady, setMpReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [publicKey, setPublicKey] = useState("");
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [payerEmail, setPayerEmail] = useState(user?.email || "");
  const [confirmedEmail, setConfirmedEmail] = useState(false);

  const meta = TIER_META[tier] || TIER_META.pro;

  useEffect(() => {
    api.get("/billing/public-key")
      .then((r) => {
        setPublicKey(r.data.public_key);
        if (r.data.public_key) {
          initMercadoPago(r.data.public_key, { locale: "pt-BR" });
          setMpReady(true);
        }
      })
      .catch(() => toast.error("Falha ao carregar configuração de pagamento"));
  }, []);

  // Detect ?billing=success (redirected back from Mercado Pago) — fetch
  // subscription, sync UI, show success screen.
  useEffect(() => {
    const billingParam = searchParams.get("billing");
    if (billingParam !== "success") return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;   // ~30s total (webhook may take a few seconds)

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get("/billing/subscription");
        if (cancelled) return;
        if (data.status === "trialing" || data.status === "active") {
          // If subscription was previously cancelled/past_due, the swap may
          // have replaced product images with placeholder. Trigger restore
          // as defensive fallback (webhook should have done it already; this
          // is idempotent).
          api.post("/billing/restore")
            .then((r) => {
              const restored = r?.data?.restored || 0;
              if (restored > 0) {
                toast.success(`${restored} produtos restaurados na sua loja!`, { duration: 8000 });
              }
            })
            .catch(() => { /* silent — restore may not be needed */ });

          setSuccess({
            tier: data.tier || tier,
            subscription_id: data.mp_subscription_id,
            trial_ends_at: data.trial_ends_at,
          });
          window.scrollTo({ top: 0, behavior: "smooth" });
          setSearchParams({}, { replace: true });
          return;
        }
      } catch { /* transient */ }
      if (attempts < maxAttempts && !cancelled) {
        setTimeout(poll, 3000);
      } else if (!cancelled) {
        toast.info("Estamos confirmando seu pagamento. Atualize a página em alguns segundos.", { duration: 10000 });
        setSearchParams({}, { replace: true });
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (formData) => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/billing/subscribe", {
        tier,
        card_token: formData.token,
        email: payerEmail || formData?.payer?.email || user?.email || "",
      });
      // If MP returned an init_point, we're in redirect mode — send user to MP
      if (data.init_point && data.status === "awaiting_authorization") {
        toast.success("Redirecionando para o Mercado Pago para autorizar o cartão...");
        setTimeout(() => { window.location.href = data.init_point; }, 800);
        return Promise.resolve();
      }
      setSuccess(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Cartão validado · trial ativo!");
      return Promise.resolve();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const rawMsg = formatApiErrorDetail(detail) || "Falha ao criar assinatura";
      let title = "Falha ao processar cartão";
      let hint = "";
      if (rawMsg.includes("Card token") || rawMsg.includes("card_token")) {
        title = "Cartão rejeitado pelo Mercado Pago";
        hint =
          "Isso costuma acontecer quando: (a) o cartão de teste não corresponde ao ambiente TEST, (b) o token expirou (>7 dias), OU (c) as chaves PUBLIC_KEY e ACCESS_TOKEN pertencem a apps diferentes no painel MP. Tente novamente clicando em 'Confirmar email' — o sistema tentará automaticamente o fluxo de redirect do MP como fallback.";
      } else if (rawMsg.includes("payer") || rawMsg.includes("email")) {
        title = "Email do comprador inválido";
        hint =
          "Para o ambiente de TESTE do Mercado Pago, o email precisa ser um usuário de teste criado em Developers → Test Users. Emails reais são rejeitados.";
      } else if (rawMsg.toLowerCase().includes("overloaded") || rawMsg.toLowerCase().includes("cloudflare")) {
        title = "Servidor temporariamente indisponível";
        hint = "O Mercado Pago está lento ou o Cloudflare timed out. Aguarde 30 segundos e tente novamente.";
      }
      setError({ title, message: rawMsg, hint });
      toast.error(title + " · " + rawMsg, { duration: 12000 });
      return Promise.reject(new Error(rawMsg));
    } finally {
      setSubmitting(false);
    }
  };

  // Redirect-only path: user clicks "Ir para pagamento" and we send to MP without card_token
  const handleRedirectPay = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/billing/subscribe", {
        tier,
        card_token: "",
        email: payerEmail || user?.email || "",
      });
      if (data.init_point) {
        toast.success("Abrindo Mercado Pago...");
        setTimeout(() => { window.location.href = data.init_point; }, 900);
      } else if (data.status === "trialing") {
        setSuccess(data);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const rawMsg = formatApiErrorDetail(detail) || "Falha ao iniciar pagamento";
      setError({ title: "Falha ao iniciar pagamento", message: rawMsg, hint: "" });
      toast.error(rawMsg, { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  // Full-page success confirmation after subscription created
  if (success) {
    const trialEnd = success.trial_ends_at ? new Date(success.trial_ends_at) : null;
    return (
      <div className="min-h-screen bg-zinc-50 flex">
        <Sidebar />
        <main className="flex-1 px-4 sm:px-8 py-12 max-w-3xl mx-auto">
          <div
            className="border border-emerald-200 bg-white rounded-md p-8 sm:p-12 text-center"
            data-testid="checkout-success-screen"
          >
            <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <div className="eyebrow mb-2 text-emerald-700">Trial ativado com sucesso</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B] mb-3">
              Bem-vindo ao Magic SIP {TIER_META[success.tier]?.name || meta.name}
            </h1>
            <p className="text-sm text-zinc-600 max-w-lg mx-auto mb-6">
              Seu cartão foi <strong>validado e cadastrado com segurança</strong> pelo Mercado Pago.
              <strong className="text-emerald-700"> Nenhum valor foi cobrado agora.</strong>{" "}
              {trialEnd && (
                <>
                  Você tem <strong>7 dias grátis</strong> para publicar até <strong>10 produtos</strong>.
                  A primeira cobrança de <strong>R$ {(TIER_META[success.tier]?.price || meta.price).toFixed(2).replace(".", ",")}</strong>
                  {" "}será feita em <strong>{trialEnd.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</strong>.
                </>
              )}
            </p>

            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 text-left mb-6 max-w-md mx-auto">
              <div className="eyebrow mb-2 text-[10px]">O que fazer agora</div>
              <ul className="space-y-2 text-xs text-zinc-700">
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Ir para <strong>Publicar</strong> e começar a subir produtos na Nuvemshop</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Acompanhar o consumo do trial em <strong>Configurações</strong> (10 produtos permitidos)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>Cancelar a qualquer momento sem multa antes de {trialEnd?.toLocaleDateString("pt-BR")}</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3 justify-center flex-wrap">
              <Button
                onClick={() => navigate("/publish")}
                className="rounded-md brand-primary text-white h-11 px-6"
                data-testid="checkout-success-publish-btn"
              >
                <Zap className="w-4 h-4 mr-2" /> Começar a publicar
              </Button>
              <Button
                onClick={() => navigate("/settings")}
                variant="outline"
                className="rounded-md h-11 px-6"
                data-testid="checkout-success-settings-btn"
              >
                Ver detalhes da assinatura
              </Button>
            </div>

            <p className="text-[10px] font-mono text-zinc-400 mt-6">
              ID Assinatura: {success.subscription_id}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      <Sidebar />
      <main className="flex-1 px-4 sm:px-8 py-8 max-w-6xl mx-auto">
        <button
          onClick={() => navigate("/pricing")}
          className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-900 mb-6 font-mono uppercase tracking-wider"
          data-testid="checkout-back-btn"
        >
          <ArrowLeft className="w-3 h-3" /> Voltar para planos
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Order summary */}
          <aside className="lg:col-span-2 space-y-4">
            <div className="border border-zinc-200 bg-white rounded-md p-6">
              <div className="eyebrow mb-2">Resumo do pedido</div>
              <div className="flex items-baseline justify-between mb-4 pb-4 border-b border-zinc-100">
                <div>
                  <div className="font-display text-2xl font-bold text-[#09090B]">
                    Magic SIP <span className="text-[#002FA7]">{meta.name}</span>
                  </div>
                  <div className="text-xs font-mono text-zinc-500 mt-1">
                    até {meta.limit.toLocaleString("pt-BR")} produtos/mês
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl font-bold text-[#09090B]">R$ {meta.price.toFixed(2).replace(".", ",")}</div>
                  <div className="text-[10px] font-mono text-zinc-500">/ mês</div>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 mb-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900 mb-1">
                  <Clock className="w-4 h-4" />
                  7 dias grátis para testar
                </div>
                <p className="text-xs text-emerald-800/80">
                  Publique até <strong>10 produtos</strong> gratuitamente. Cobrança de <strong>R$ {meta.price.toFixed(2).replace(".", ",")}</strong> só no 8º dia.
                  Cancele quando quiser sem multa.
                </p>
              </div>

              <div className="space-y-2 text-xs text-zinc-600">
                <div className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-[#002FA7] mt-0.5" />
                  <span>Cartão obrigatório para validar identidade — não cobra durante o trial</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-[#002FA7] mt-0.5" />
                  <span>PIX / Boleto também disponíveis (sem trial nessa modalidade)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-[#002FA7] mt-0.5" />
                  <span>Cancele nas Configurações a qualquer momento</span>
                </div>
                <div className="flex items-start gap-2">
                  <Shield className="w-3.5 h-3.5 text-[#002FA7] mt-0.5" />
                  <span>Dados criptografados via Mercado Pago (PCI-DSS)</span>
                </div>
              </div>
            </div>

            {/* Trocar plano */}
            <div className="border border-zinc-200 bg-white rounded-md p-4">
              <div className="eyebrow mb-2 text-[10px]">Trocar de plano</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="checkout-tier-switcher">
                {Object.entries(TIER_META).map(([id, m]) => (
                  <button
                    key={id}
                    onClick={() => { setTier(id); navigate(`/checkout/${id}`, { replace: true }); }}
                    className={`text-left border rounded p-2 transition ${
                      tier === id
                        ? "border-[#002FA7] bg-[#002FA7]/5"
                        : "border-zinc-200 hover:border-zinc-400"
                    }`}
                    data-testid={`checkout-tier-${id}`}
                  >
                    <div className="text-[10px] font-mono uppercase text-zinc-500">{m.name}</div>
                    <div className="text-sm font-semibold text-[#09090B]">R$ {m.price.toFixed(2).replace(".", ",")}</div>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Payment form */}
          <section className="lg:col-span-3">
            {/* Persistent error banner */}
            {error && (
              <div
                className="border border-red-300 bg-red-50 rounded-md p-4 mb-4 flex items-start gap-3"
                data-testid="checkout-error-banner"
              >
                <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-xs text-red-900">
                  <div className="font-semibold text-sm mb-1">{error.title}</div>
                  <p className="mb-2 font-mono break-words">{error.message}</p>
                  {error.hint && (
                    <p className="text-red-800 bg-red-100 p-2 rounded border border-red-200">
                      💡 <strong>Como resolver:</strong> {error.hint}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-800 text-xs font-mono uppercase"
                  data-testid="checkout-error-dismiss"
                >
                  fechar
                </button>
              </div>
            )}

            <div className="border border-zinc-200 bg-white rounded-md p-6">
              <div className="eyebrow mb-4 flex items-center gap-2">
                <Zap className="w-3 h-3" />
                Pagamento seguro · Mercado Pago
              </div>
              <h1 className="font-display text-2xl font-bold text-[#09090B] mb-1">
                Cadastre seu cartão para começar o trial
              </h1>
              <p className="text-xs text-zinc-500 mb-6">
                Nenhum valor é cobrado agora. Você tem 7 dias grátis para testar até 10 produtos.
              </p>

              {/* Payer email (editable — critical for MP TEST environment) */}
              <div className="mb-6" data-testid="checkout-payer-email-section">
                <label htmlFor="payer-email" className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
                  Email do comprador
                </label>
                <input
                  id="payer-email"
                  type="email"
                  value={payerEmail}
                  onChange={(e) => setPayerEmail(e.target.value)}
                  placeholder="seu-email@exemplo.com"
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none font-mono"
                  data-testid="checkout-payer-email-input"
                />
                <p className="text-[10px] font-mono text-zinc-500 mt-1.5">
                  Este email será associado à sua assinatura no Mercado Pago
                </p>
              </div>

              <div className="mb-6">
                <Button
                  onClick={() => {
                    const trimmed = payerEmail.trim();
                    if (!trimmed || !trimmed.includes("@") || !trimmed.includes(".")) {
                      toast.error("Digite um email válido antes de continuar");
                      return;
                    }
                    if (trimmed !== payerEmail) setPayerEmail(trimmed);
                    handleRedirectPay();
                  }}
                  disabled={submitting || !payerEmail.trim() || !payerEmail.includes("@") || !payerEmail.includes(".")}
                  className="w-full h-12 rounded-md brand-primary text-white text-base font-medium disabled:bg-zinc-300 disabled:cursor-not-allowed"
                  data-testid="checkout-redirect-pay-btn"
                >
                  {submitting ? (
                    <><Clock className="w-4 h-4 mr-2 animate-spin" /> Preparando...</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" /> Ir para pagamento no Mercado Pago →</>
                  )}
                </Button>
                <p className="text-[10px] font-mono text-zinc-500 text-center mt-2">
                  Você será redirecionado para o site oficial do Mercado Pago para inserir os dados do cartão, PIX ou boleto — mais seguro e aceita mais formas de pagamento
                </p>
                <div className="mt-4 border border-amber-200 bg-amber-50 rounded-md p-3 text-[11px] text-amber-900 leading-relaxed">
                  <strong>Pagando por PIX?</strong> Depois de confirmar o pagamento no Mercado Pago,
                  clique no botão <strong>"Voltar ao site"</strong> na tela do MP para retornar ao
                  Magic SIP. Se você fechou a aba, clique no botão abaixo para verificar o status.
                </div>
                <Button
                  variant="outline"
                  className="w-full h-10 mt-3 rounded-md border-zinc-300 text-sm"
                  data-testid="checkout-already-paid-btn"
                  onClick={async () => {
                    toast.info("Consultando status do pagamento...");
                    try {
                      const { data } = await api.get("/billing/subscription");
                      if (data.status === "trialing" || data.status === "active") {
                        setSuccess({
                          tier: data.tier || tier,
                          subscription_id: data.mp_subscription_id,
                          trial_ends_at: data.trial_ends_at,
                        });
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        toast.success("Assinatura ativa! Bem-vindo(a) ao Magic SIP.");
                      } else if (data.status === "awaiting_authorization") {
                        toast.info("Mercado Pago ainda não confirmou. Aguarde 30s e tente novamente. Um PIX geralmente é confirmado em segundos.", { duration: 10000 });
                      } else {
                        toast.info(`Status atual: ${data.status || "sem assinatura"}. Se você pagou agora, aguarde 30s e clique de novo.`, { duration: 10000 });
                      }
                    } catch (e) {
                      toast.error("Falha ao consultar status. Tente novamente em alguns segundos.");
                    }
                  }}
                >
                  Já paguei — verificar status
                </Button>
              </div>

              {submitting && (
                <div className="mt-4 text-center text-xs text-zinc-500 font-mono">
                  Processando... aguarde.
                </div>
              )}
            </div>

            <p className="text-[10px] font-mono text-zinc-400 text-center mt-4">
              Ao confirmar, você aceita os termos do Magic SIP. A cobrança recorrente pode ser cancelada a qualquer momento em Configurações.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
